import type { AdminUserSummary } from '@/api';
import { formatRelativeAge, formatTokenCount, formatUsd } from '@/lib/format';
import { formatCountdown } from '@/lib/tempAccount';
import { accountDay } from '@/lib/userStats';
import {
  ChevronRight,
  Eraser,
  KeyRound,
  Loader2,
  RotateCcw,
  ScrollText,
  Trash2
} from 'lucide-react';

import type { UserRowHandlers } from '@/components/admin/userRow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

type Props = { row: AdminUserSummary; handlers: UserRowHandlers };

/**
 * One user as a tappable card with inline controls — the mobile list row.
 *
 * The admin pane is a narrow drawer on mobile, where the dense table would need
 * horizontal scrolling, so the switches and action icons stay on the row.
 */
export default function UserCard({ row, handlers }: Props) {
  const { isSelf, busy } = handlers;
  const anyBusy = busy !== null;
  const joined = accountDay(row.createdAt);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={handlers.onOpenDetail}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handlers.onOpenDetail();
        }
      }}
      className="cursor-pointer transition-colors hover:bg-accent/30 hover:border-primary/30"
    >
      <CardContent className="p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium truncate flex items-center gap-2">
              {row.identifier}
              {isSelf ? (
                <Badge variant="outline" className="text-[10px]">
                  you
                </Badge>
              ) : null}
              {row.role === 'admin' ? (
                <Badge className="text-[10px]">admin</Badge>
              ) : null}
              {row.disabled ? (
                <Badge variant="destructive" className="text-[10px]">
                  disabled
                </Badge>
              ) : null}
              {row.is_temporary ? (
                <Badge variant="secondary" className="text-[10px]">
                  temp · {formatCountdown(row.temp_expires_at)}
                </Badge>
              ) : null}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <span>{row.provider || 'unknown provider'}</span>
              {row.total_tokens ? (
                <span className="font-mono tabular-nums">
                  · {formatTokenCount(row.total_tokens)} tok
                </span>
              ) : null}
              {row.total_cost ? (
                <span className="font-mono tabular-nums">
                  · {formatUsd(row.total_cost)}
                </span>
              ) : null}
            </div>
            <div className="text-xs text-muted-foreground">
              {joined ? `joined ${joined}` : 'join date unknown'}
              {' · '}
              {row.last_login_at
                ? `seen ${formatRelativeAge(row.last_login_at)}`
                : 'never logged in'}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>
        <div
          className="flex flex-col gap-2 pt-1"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={row.role === 'admin'}
                disabled={anyBusy || (isSelf && row.role === 'admin')}
                onCheckedChange={handlers.onToggleAdmin}
              />
              <span>Admin</span>
              {busy === 'role' ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : null}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={row.disabled}
                disabled={anyBusy || isSelf}
                onCheckedChange={handlers.onToggleDisabled}
              />
              <span>Disabled</span>
              {busy === 'disabled' ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : null}
            </label>
          </div>
          <div className="flex items-center justify-end gap-1 border-t pt-2">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={handlers.onAuditLog}
              disabled={anyBusy}
              aria-label="View audit log"
              title="View audit log"
            >
              <ScrollText className="h-3 w-3" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={handlers.onPasswordReset}
              disabled={anyBusy}
              aria-label="Send password reset email"
              title="Send password reset email"
            >
              {busy === 'reset' ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <KeyRound className="h-3 w-3" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={handlers.onLegalReset}
              disabled={anyBusy}
              aria-label="Debug: Reset legal disclaimer"
              title="Debug: Reset legal disclaimer"
            >
              {busy === 'legal-reset' ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RotateCcw className="h-3 w-3" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={handlers.onPurge}
              disabled={anyBusy || isSelf}
              aria-label="Delete user and all data"
              title="Delete user & all data"
            >
              {busy === 'purge' ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Eraser className="h-3 w-3" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={handlers.onDelete}
              disabled={anyBusy || isSelf}
              aria-label="Delete user"
              title="Delete user"
            >
              {busy === 'delete' ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
